import { useCallback, useEffect, useState } from 'react';
import { stateSetter } from '@/utils';
import { IServerColumnConfig } from '@/components/table/column-config';
import { IFspLight } from '../use-project-types/types';
import {
	fetchInvitationsTableConfig,
	fetchInvitationAdministrations,
	fetchInvitationStatuses,
	fetchInvitations,
	fetchSingleInvitation,
	fetchSingleInvitationLogs,
	postInvitation,
	resendStoredInvitation,
	revokeStoredInvitation
} from './actions';
import {
	CreateInvitationPayload,
	IInvitationStatusInfo,
	IInvitationsQuery,
	InvitationsReturnType,
	Invitatie
} from './types';

export function useInvitations<T extends Invitatie | Invitatie[]>(
	getInvitationsOnRender: boolean | undefined = undefined,
	invitationId: number | string | undefined = undefined,
	queryParam: IInvitationsQuery | undefined = undefined,
	/**
	 * Paginarea INIȚIALĂ (de regulă cea din URL, via useListQueryState). Fără ea,
	 * starea internă pornește pe 0/10 și primul fetch pleacă cu valori greșite, urmat
	 * de un al doilea după sincronizarea din efectele-punte — două call-uri și un race
	 * pe răspunsuri. Doar valoarea de la primul render contează.
	 */
	initialPagination: { page: number; items: number } | undefined = undefined,
	withTableConfig = false
): InvitationsReturnType<T> {
	const [data, setData] = useState<T | null>(null);
	// Configurația de coloane (api/invitations/table-config) — cerută doar de pagina de
	// listă (withTableConfig); la eroare rămâne null și tabelul cade silențios pe
	// coloanele definite local.
	const [tableConfig, setTableConfig] = useState<IServerColumnConfig[] | null>(null);
	const [loadingTableConfig, setLoadingTableConfig] = useState<boolean>(
		Boolean(withTableConfig)
	);

	const [loading, setLoading] = useState<boolean>(false);
	const [loadingCTA, setLoadingCTA] = useState<boolean>(false);
	const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
	const [administrations, setAdministrations] = useState<IFspLight[]>([]);
	const [loadingAdministrations, setLoadingAdministrations] = useState<boolean>(false);
	const [statuses, setStatuses] = useState<IInvitationStatusInfo[]>([]);
	const [loadingStatuses, setLoadingStatuses] = useState<boolean>(false);
	const [page, setPage] = useState<number>(initialPagination?.page ?? 0);
	const [items, setItems] = useState<number>(initialPagination?.items ?? 10);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(0);

	useEffect(() => {
		if (!withTableConfig) return undefined;
		let active = true;
		setLoadingTableConfig(true);
		fetchInvitationsTableConfig()
			.then((columns) => {
				if (active) setTableConfig(columns.length ? columns : null);
			})
			.catch(() => {
				// Fallback silențios: fără config BE, tabelul folosește coloanele locale.
				if (active) setTableConfig(null);
			})
			.finally(() => {
				if (active) setLoadingTableConfig(false);
			});
		return () => {
			active = false;
		};
	}, [withTableConfig]);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam || 1);
		setTotalItems(itemsParam || 0);
	};

	const getInvitationsList = useCallback(
		async (
			nextQueryParam: IInvitationsQuery | undefined = queryParam,
			pageParam: number = page,
			itemsParam: number = items
		) => {
			try {
				setLoading(true);
				const response = await fetchInvitations(
					nextQueryParam,
					pageParam,
					itemsParam,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		[queryParam, page, items]
	);

	const getInvitation = useCallback(async (currentInvitationId: number | string) => {
		try {
			setLoading(true);
			const [invitationResponse, logsResponse] = await Promise.all([
				fetchSingleInvitation(currentInvitationId),
				fetchSingleInvitationLogs(currentInvitationId)
			]);
			const response: Invitatie = {
				...invitationResponse,
				istoric:
					logsResponse === null
						? invitationResponse.istoric
						: logsResponse.length > 0
							? logsResponse
							: invitationResponse.istoric
			};
			setData(response as T);
		} catch {
			setData(null);
		} finally {
			setLoading(false);
		}
	}, []);

	const getAdministrations = useCallback(async () => {
		try {
			setLoadingAdministrations(true);
			const response = await fetchInvitationAdministrations();
			setAdministrations(response);
		} catch {
			// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
		} finally {
			setLoadingAdministrations(false);
		}
	}, []);

	const getStatuses = useCallback(async () => {
		try {
			setLoadingStatuses(true);
			const response = await fetchInvitationStatuses();
			setStatuses(response);
		} catch {
			// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
		} finally {
			setLoadingStatuses(false);
		}
	}, []);

	const createInvitation = useCallback(
		async (
			payload: CreateInvitationPayload,
			onSuccess?: (invitation: Invitatie) => void
		): Promise<boolean> => {
			try {
				setLoadingCreate(true);
				const response = await postInvitation(payload);
				setData((currentData) => {
					if (Array.isArray(currentData)) {
						return [response, ...currentData] as T;
					}

					return currentData;
				});
				setTotalItems((current) => current + 1);
				onSuccess?.(response);
				return true;
			} catch {
				return false;
			} finally {
				setLoadingCreate(false);
			}
		},
		[]
	);

	const resendInvitation = useCallback(
		async (
			currentInvitationId: number | string,
			onSuccess?: (invitation: Invitatie) => void
		): Promise<boolean> => {
			try {
				setLoadingCTA(true);
				const response = await resendStoredInvitation(currentInvitationId);
				setData((currentData) => {
					if (Array.isArray(currentData)) {
						return currentData.map((invitation) =>
							String(invitation.id) === String(currentInvitationId) ? response : invitation
						) as T;
					}

					if (
						currentData &&
						!Array.isArray(currentData) &&
						String(currentData.id) === String(currentInvitationId)
					) {
						return response as T;
					}

					return currentData;
				});
				onSuccess?.(response);
				return true;
			} catch {
				return false;
			} finally {
				setLoadingCTA(false);
			}
		},
		[]
	);

	const revokeInvitation = useCallback(
		async (
			currentInvitationId: number | string,
			onSuccess?: (invitation: Invitatie) => void
		): Promise<boolean> => {
			try {
				setLoadingCTA(true);
				const response = await revokeStoredInvitation(currentInvitationId);
				setData((currentData) => {
					if (Array.isArray(currentData)) {
						return currentData.map((invitation) =>
							String(invitation.id) === String(currentInvitationId) ? response : invitation
						) as T;
					}

					if (
						currentData &&
						!Array.isArray(currentData) &&
						String(currentData.id) === String(currentInvitationId)
					) {
						return response as T;
					}

					return currentData;
				});
				onSuccess?.(response);
				return true;
			} catch {
				return false;
			} finally {
				setLoadingCTA(false);
			}
		},
		[]
	);

	useEffect(() => {
		if (getInvitationsOnRender && invitationId === undefined) {
			getInvitationsList(queryParam, page, items);
		}
	}, [
		getInvitationsOnRender,
		invitationId,
		queryParam,
		page,
		items,
		getInvitationsList
	]);

	useEffect(() => {
		if (getInvitationsOnRender && invitationId === undefined) {
			getAdministrations();
			getStatuses();
		}
	}, [getInvitationsOnRender, invitationId, getAdministrations, getStatuses]);

	useEffect(() => {
		if (invitationId !== undefined) {
			getInvitation(invitationId);
		}
	}, [invitationId, getInvitation]);

	return {
		tableConfig,
		loadingTableConfig,
		data,
		setData: setData as stateSetter<T | null>,
		loading,
		loadingCTA,
		loadingCreate,
		administrations,
		loadingAdministrations,
		statuses,
		loadingStatuses,
		getInvitations: getInvitationsList,
		getInvitation,
		getAdministrations,
		getStatuses,
		createInvitation,
		resendInvitation,
		revokeInvitation,
		page,
		items,
		setPage,
		setItems,
		totalPages,
		totalItems
	};
}
